import { Injectable } from '@angular/core';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class TicketingApiService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {}

    /**
     * Add a new ticket
     */
    async postTicket(stationId: string, ticketId: string, body: PostTicketRequest): Promise<PostTicketResponse> {
        return this.http
            .post<PostTicketResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get a specific ticket
     */
    async getTicket(stationId: string, ticketId: string): Promise<GetTicketResponse> {
        return this.http
            .get<GetTicketResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get all tickets for station
     */
    async getTickets(stationId: string): Promise<GetTicketsResponse> {
        return this.http
            .get<GetTicketsResponse>(`${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing`, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * Get all tickets
     */
    async getAllTickets(queryParams?: GetAllTicketsQueryParameters): Promise<GetAllTicketsResponse> {
        return this.http
            .get<GetAllTicketsResponse>(
                `${this.config.restApiUrl}/ticketing?${Object.keys(queryParams || {})
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Modify ticket
     */
    async patchTicket(stationId: string, ticketId: string, body: PatchTicketRequest): Promise<PatchTicketResponse> {
        return this.http
            .patch<PatchTicketResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Delete ticket
     */
    async deleteTicket(stationId: string, ticketId: string): Promise<DeleteTicketResponse> {
        return this.http
            .delete<DeleteTicketResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Add a new comment
     */
    async postComment(stationId: string, ticketId: string, body: PostCommentRequest): Promise<PostCommentResponse> {
        return this.http
            .post<PostCommentResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}/comment`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Modify comment
     */
    async patchComment(
        stationId: string,
        ticketId: string,
        commentId: string,
        body: PatchCommentRequest
    ): Promise<PatchCommentResponse> {
        return this.http
            .patch<PatchCommentResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}/comment/${encodeURIComponent(commentId)}`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Delete comment
     */
    async deleteComment(stationId: string, ticketId: string, commentId: string): Promise<DeleteCommentResponse> {
        return this.http
            .delete<DeleteCommentResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}/comment/${encodeURIComponent(commentId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Add new attachment
     */
    async postAttachment(
        stationId: string,
        ticketId: string,
        body: PostAttachmentRequest
    ): Promise<PostAttachmentResponse> {
        return this.http
            .post<PostAttachmentResponse>(
                `${this.config.restApiUrl}/stations/${encodeURIComponent(stationId)}/ticketing/${encodeURIComponent(ticketId)}/attachment`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }
}

export interface DeleteCommentResponse {
    success: boolean;
}

export interface DeleteTicketResponse {
    success: boolean;
}

export interface GetTicketResponse {
    ticket?: TicketWithComments;
}

export interface GetTicketsResponse {
    tickets: Ticket[];
}

export interface TicketCommentPatch {
    attachments?: TicketAttachmentBase[];
    message?: string;
}

export interface PatchCommentRequest {
    comment: PartialPickTicketComment;
}

export interface PatchCommentResponse {
    comment: TicketComment;
}

export interface TicketPatch {
    title?: string;
    description?: string;
    status?: 'CLOSED' | 'OPEN';
    attachments?: TicketAttachmentBase[];
}

export interface PatchTicketRequest {
    ticket: PartialPickTicket;
}

export interface PatchTicketResponse {
    ticket: Ticket;
}

export interface PostAttachmentRequest {
    attachments: TicketAttachmentBase[];
    /**
     * for what comment this attachment belongs
     */
    commentId?: string;
}

export interface PostAttachmentResponse {
    attachments: {
        /**
         * Original name of the file
         */
        fileName: string;
        fileSize: number;
        contentType: string;
        /**
         * UUID
         */
        fileId: string;
        temporaryUploadUrl: string;
    }[];
}

export interface PostCommentRequest {
    comment: PickTicketComment;
}

export interface PostCommentResponse {
    comment: {
        _pk: string;
        _sk: string;
        _gsiTenantEMSPPk: string;
        _gsiTenantEMSPSk: string;
        _gsiTenantCPOPk: string;
        _gsiTenantCPOSk: string;
        _gsiTenantMaintenancePk: string;
        _gsiTenantMaintenanceSk: string;
        _gsi1pk: string;
        _gsi1sk: string;
        _gsi2pk: string;
        _gsi2sk: string;
        /**
         * DynamoDB TTL (Time to Live) - item expiry time (unix timestamp).
         * Set for scheduled deletion of item row.
         * Please note this is SECONDS (unix timestamp) not MILLISECONDS (like usually in javascript).
         */
        _ttl?: number;
        tenantIdEMSP: string;
        tenantIdCPO: string;
        tenantIdMaintenance: string;
        schema: 'ticketComment';
        schemaVersion?: number;
        versionCreatedTimestamp: string;
        versionCreatedUserId: string;
        versionCreatedUserRole: string;
        versionCreatedTenantId: string;
        versionModifiedTimestamp: string;
        versionModifiedUserId: string;
        versionDeletedTimestamp?: string;
        versionDeletedUserId: string;
        /**
         * Which tenants has access to this row
         */
        tenantIds: string[];
        deleted?: boolean;
        attachments?: TicketAttachmentBase[];
        commentDate: string;
        commenter: TicketUserBase;
        commentId: string;
        message: string;
        stationId: string;
        ticketId: string;
    };
}

export interface PostTicketRequest {
    ticket: PickTicket;
}

export interface PostTicketResponse {
    created: boolean;
}

export interface GetAllTicketsResponse {
    tickets: Ticket[];
}

export interface GetAllTicketsQueryParameters {
    status?: 'CLOSED' | 'OPEN';
}

export interface TicketWithComments {
    attachments: TicketAttachment[];
    comments: TicketComment[];
    ref: string;
    description?: string;
    reportDate: string;
    reporter: TicketUserBase;
    stationId: string;
    status: 'CLOSED' | 'OPEN';
    ticketId: string;
    title: string;
    ticketOrigin?: 'ChargEye' | 'Linked';
    ticketOriginPublicLink?: string;
    ticketOriginInternalLink?: string;
}

export interface TicketAttachment {
    temporaryUrl: string;
    /**
     * Original name of the file
     */
    fileName: string;
    fileSize: number;
    contentType: string;
    /**
     * UUID
     */
    fileId: string;
}

export interface TicketComment {
    attachments?: TicketAttachmentBase[];
    commentDate?: string;
    commenter: TicketUserBase;
    commentId: string;
    message: string;
    stationId?: string;
    ticketId?: string;
    public?: boolean;
}

export interface TicketAttachmentBase {
    /**
     * Original name of the file
     */
    fileName: string;
    fileSize: number;
    contentType: string;
    /**
     * UUID
     */
    fileId: string;
}

export interface TicketUserBase {
    /**
     * Full name of the user
     */
    name: string;
    /**
     * Id of the user
     */
    id: string;
}

export interface Ticket {
    ref: string;
    attachments?: TicketAttachmentBase[];
    description?: string;
    reportDate: string;
    reporter: TicketUserBase;
    stationId: string;
    status: 'CLOSED' | 'OPEN';
    ticketId: string;
    title: string;
    ticketOrigin?: 'ChargEye' | 'Linked';
    ticketOriginPublicLink?: string;
    ticketOriginInternalLink?: string;
}

export type RowTypes =
    | 'LBDecisionLog'
    | 'LBPowerGroupSample'
    | 'LBStation'
    | 'LBStationStatus'
    | 'LBpowerGroup'
    | 'actualEvse'
    | 'alert'
    | 'alertHistory'
    | 'auditLog'
    | 'authorizationList'
    | 'authorizationUserCollection'
    | 'chargerPowerReservation'
    | 'commissioning'
    | 'connection'
    | 'connectionHistory'
    | 'connectionTimestamp'
    | 'counter'
    | 'device'
    | 'deviceHistory'
    | 'ev'
    | 'evControlLog'
    | 'evSchedule'
    | 'evScheduleHistory'
    | 'evScheduleRecurrence'
    | 'evse'
    | 'externalNotification'
    | 'externalNotificationSettings'
    | 'firmwarePackage'
    | 'group'
    | 'integrationSettings'
    | 'location'
    | 'locationSettings'
    | 'logStream'
    | 'notificationSettings'
    | 'ocpiConnectionProfile'
    | 'ocpiLog'
    | 'ocpiSequencer'
    | 'ocppMessage'
    | 'ocppRequest'
    | 'onlineStatus'
    | 'remoteStart'
    | 'settings'
    | 'settingsHistory'
    | 'settingsProfile'
    | 'sitePowerMeasurementSample'
    | 'station'
    | 'stationInventory'
    | 'stationMatrix'
    | 'stationTariffs'
    | 'stationTenancy'
    | 'status'
    | 'statusHistory'
    | 'systemHealth'
    | 'systemMetrics'
    | 'systemMetricsHistory'
    | 'systemStructure'
    | 'systemStructureHistory'
    | 'tariff'
    | 'ticket'
    | 'ticketComment'
    | 'tx'
    | 'txFeedback'
    | 'txLatest'
    | 'txSample'
    | 'txUpdate'
    | 'updateProcess'
    | 'updateProcessNotification'
    | 'userCollection'
    | 'userCollectionToken'
    | 'v2icp';

export interface PartialPickTicketComment {
    attachments?: TicketAttachmentBase[];
    message?: string;
}

export interface PartialPickTicket {
    title?: string;
    description?: string;
    status?: 'CLOSED' | 'OPEN';
    attachments?: TicketAttachmentBase[];
}

export interface PickTicketComment {
    attachments?: TicketAttachmentBase[];
    message: string;
    commentId: string;
}

export interface PickTicket {
    title: string;
    description?: string;
    status: 'CLOSED' | 'OPEN';
    attachments?: TicketAttachmentBase[];
    ticketOrigin?: 'ChargEye' | 'Linked';
    ticketOriginPublicLink?: string;
    ticketOriginInternalLink?: string;
}
